import { FunctionComponent } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import Pet from '@spike/pet-model';
import {
    faCat,
    faDog,
    faRuler,
    faWeightScale,
    faNarwhal,
    faHeartPulse,
    faAngry,
    faEar,
    faMeh,
    faPoo,
    faSmile
} from '@fortawesome/pro-light-svg-icons';
import { faTriangleExclamation, faRampLoading } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import ItemLabelIcon from 'components/UI/ItemLabelIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip2 as Chip } from 'components/UI';
import { useAuth } from 'hooks';
import { Auth } from '@spike/auth-model';

interface PetHeaderProps {
    pet?: Pet;
    onShowPetCard?: () => void;
    className?: string;
    petSizeClassName?: string;
    petItemLabelClassName?: string;
    showWeight?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            alignItems: 'center',
            width: '100%'
        },
        petHeader: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start'
        },
        petPopUp: {
            cursor: 'pointer',
            paddingRight: 18,
            [theme.breakpoints.up('md')]: {
                paddingRight: 20
            }
        },
        typoPetName: {
            fontWeight: 600,
            fontSize: 16,
            width: 'auto'
        },
        hover: {
            '&:hover': {
                textDecoration: 'underline'
            }
        },
        boxFlex: {
            display: 'flex'
        },
        personalitiesContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            marginTop: 5
        },
        itemLabelBreed: {
            fontSize: '14px !important',
            width: '90%',
            paddingRight: 8,
            [theme.breakpoints.up('md')]: {
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden'
            }
        },
        itemLabel: {
            fontSize: '14px !important',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            width: '90%',
            overflow: 'hidden',
            paddingRight: 8
        },
        item: {
            [theme.breakpoints.between(360, 500)]: {
                width: '45%'
            }
        },
        itemBreed: {
            '& > *': {
                alignItems: 'start !important'
            },
            [theme.breakpoints.between(360, 500)]: {
                width: '45%'
            }
        },
        heartIcon: {
            color: '#EF4F57',
            fontWeight: 300,
            fontSize: 17,
            marginLeft: 5,
            [theme.breakpoints.up('md')]: {
                fontSize: 19,
                marginLeft: 7
            }
        },
        specialCareIcon: {
            color: '#ECC915',
            fontWeight: 300,
            fontSize: 17,
            marginLeft: 5,
            [theme.breakpoints.up('md')]: {
                fontSize: 19,
                marginLeft: 7
            }
        },
        deceasedIcon: {
            color: '#EF4F57',
            fontWeight: 300,
            fontSize: 17,
            marginLeft: 5,
            position: 'relative',
            top: '-2px'
        },
        chip: {
            'width': 'auto',
            'height': 24,
            'marginBottom': 8,
            'marginRight': 8,
            'cursor': 'default',
            '& .MuiTypography-body1': {
                fontWeight: 500,
                fontSize: '14px !important'
            }
        }
    })
);

const icons = new Map([
    ['faAngry', faAngry],
    ['faMeh', faMeh],
    ['faSmile', faSmile],
    ['faEar', faEar],
    ['faPoo', faPoo]
]);

export const PetDescription: FunctionComponent<PetHeaderProps> = props => {
    const classes = useStyles(props);

    const loadPetTags = () => {
        const positive = props.pet?.positivePersonalities || [];
        const negative = props.pet?.negativePersonalities || [];

        const combined = [...positive, ...negative];

        const sorted = combined.slice().sort((a, b) => a.name.localeCompare(b.name));

        return sorted;
    };

    const auth: Auth = useAuth();

    const petTagsAll = loadPetTags();

    const petWeight = Number(props.pet?.weight);
    const weightLabel = Number.isFinite(petWeight) && petWeight > 0 ? `${Math.trunc(petWeight)} Lbs` : '—';
    const sizeLabel = props.pet?.size?.name ? props.pet.size.name : '—';

    return (
        <Box>
            {!props.pet ? (
                <CircularProgress
                    size="1.5rem"
                    style={{ color: '#EAB464' }}
                />
            ) : (
                <Box className={clsx(classes.container, props.className)}>
                    <Box className={classes.petHeader}>
                        <Box className={clsx(classes.petPopUp, classes.boxFlex)}>
                            <Typography
                                className={clsx(classes.typoPetName, {
                                    [classes.hover]: props.onShowPetCard
                                })}
                                onClick={() => props.onShowPetCard && props.onShowPetCard()}
                            >
                                {props.pet?.name}
                            </Typography>
                            {props.pet?.deceased && <Typography className={classes.deceasedIcon}>🌈</Typography>}
                            {props.pet?.hasMedicalConditions && (
                                <FontAwesomeIcon
                                    icon={faHeartPulse}
                                    className={classes.heartIcon}
                                />
                            )}
                            {auth.user !== undefined && props.pet?.negativePersonalities?.length! > 0 && (
                                <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                    className={classes.specialCareIcon}
                                />
                            )}
                        </Box>
                        <Box className={clsx(classes.boxFlex, props.petSizeClassName)}>
                            <ItemLabelIcon
                                label={props.pet?.breed?.name ? props.pet?.breed?.name : '—'}
                                icon={
                                    props.pet?.type?.singular === 'Cat'
                                        ? faCat
                                        : props.pet?.type?.singular === 'Dog'
                                        ? faDog
                                        : faNarwhal
                                }
                                className={clsx(
                                    props.pet?.breed?.name.trim().includes(' ') ? classes.itemBreed : classes.item,
                                    props.petItemLabelClassName
                                )}
                                classNameLabel={
                                    props.pet?.breed?.name.trim().includes(' ')
                                        ? classes.itemLabelBreed
                                        : classes.itemLabel
                                }
                            />
                            <ItemLabelIcon
                                label={props.showWeight ? weightLabel : sizeLabel}
                                icon={props.showWeight ? faWeightScale : faRuler}
                                classNameLabel={classes.itemLabel}
                                className={props.petItemLabelClassName}
                            />
                        </Box>
                        <Box className={classes.personalitiesContainer}>
                            {auth.user !== undefined &&
                                petTagsAll?.map(petTag => (
                                    <Chip
                                        key={petTag.id}
                                        label={petTag.name}
                                        className={classes.chip}
                                    />
                                ))}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default PetDescription;
